import React from 'react';
import {
  addSignCommaFormat,
  commaFormat,
  getSign,
  Sign,
  SignAddedCommaFormattedString,
} from '../../utils/formatter';
import GoogleChart from 'react-google-charts';
import { GoogleChartOptions } from 'react-google-charts/dist/types';
import * as api from 'config/api';
import * as consts from 'config/consts';
import { AssetType, productTypes } from 'config/consts';
import { CurrencyMode, useCurrencyMode } from 'hooks/useCurrencyMode';
import { useRouting } from 'hooks/useRouting';
import {
  AboutExchangeRateModalButton,
  DataFetchingState,
  getDataFetchingState,
} from 'components/features/home/PortfolioStuffing/asset/PortfolioStuffingAsset';
import {
  getDataFetchingTextStyle_,
  ToggleButton,
} from 'components/features/home/PortfolioRatioStuffing/PortfolioRatioStuffingList';
import {
  isForeignStockTypes,
  ProductTypesLabel,
} from 'components/features/utils/productTypes';
import { isGoSymbolDetail } from 'components/features/utils/util';

const SORTED = Symbol('SORTED');
type SortedList<T> = { [key in typeof SORTED]: never } & T[];
const sort = <T,>(v: T[], sortf: (a: T, b: T) => number): SortedList<T> => {
  return v.sort(sortf) as SortedList<T>;
};

type GetRankingData = (
  data: SortedList<
    Omit<ProfitAndLossCardProps, 'currencyMode'> & {
      rawProfitAndLoss: ProfitAndLossPercentage;
    }
  >
) => RawData;
export type ProfitAndLossValuationRankingProps = {
  increaseProfitAndLossData: SortedList<
    Omit<ProfitAndLossCardProps, 'currencyMode'> & {
      rawProfitAndLoss: ProfitAndLossPercentage;
    }
  >;
  decreaseProfitAndLossData: SortedList<
    Omit<ProfitAndLossCardProps, 'currencyMode'> & {
      rawProfitAndLoss: ProfitAndLossPercentage;
    }
  >;
  Chart: React.FC<BarChartProps>;
};

type CashMarginProps = number;
type ProductTypeProps = number;

type ProfitAndLossCardProps = {
  stockCode: string;
  stockName: string;
  valuation: number | null;
  profitAndLossAmount: SignAddedCommaFormattedString;
  profitAndLossSign: Sign;
  profitAndLossRate: SignAddedCommaFormattedString;
  cashMargin: CashMarginProps;
  currencyMode: CurrencyMode;
  productType: ProductTypeProps;
  foreign: {
    currency: string | null;
    valuation: number | null;
    profitAndLossAmount: SignAddedCommaFormattedString;
    profitAndLossSign: Sign;
    profitAndLossRate: SignAddedCommaFormattedString;
  } | null;
};

type ProfitAndLossCardListProps = {
  children: ReturnType<React.FC<ProfitAndLossCardProps>>[];
};

type TitleProps = {
  children: string;
};

const ALIGN = {
  INCREASE: 'left',
  DECREASE: 'right',
} as const;
type Align = typeof ALIGN[keyof typeof ALIGN];
type Rank = 1 | 2 | 3 | 4 | 5;
const PROFIT_AND_LOSS = Symbol('PROFIT_AND_LOSS');
type ProfitAndLossPercentage = { [key in typeof PROFIT_AND_LOSS]: never } &
  number;
const createProfitAndLossPercentage = (
  v: number,
  base: number
): ProfitAndLossPercentage => (v / base) as ProfitAndLossPercentage;
type Tooltip = SignAddedCommaFormattedString;
type BarChartRowData = [Rank, ProfitAndLossPercentage, Tooltip];
const getRank = (data: BarChartRowData): Rank => {
  return data[0];
};
type BarChartData = BarChartRowData[];
type BarChartProps = {
  align: Align;
  data: BarChartData;
};

type LabelsRowData = { rank: Rank; stockName: string };
type LabelsData = LabelsRowData[];
type BarChartLabelsProps = {
  data: LabelsData;
};

type NoRankingItemProps = {
  align: Align;
};
type RawData =
  | [
      {
        rank: 1;
        profitAndLoss: ProfitAndLossPercentage;
        profitAndLossString: SignAddedCommaFormattedString;
        stockName: string;
      },
      {
        rank: 2;
        profitAndLoss: ProfitAndLossPercentage;
        profitAndLossString: SignAddedCommaFormattedString;
        stockName: string;
      },
      {
        rank: 3;
        profitAndLoss: ProfitAndLossPercentage;
        profitAndLossString: SignAddedCommaFormattedString;
        stockName: string;
      },
      {
        rank: 4;
        profitAndLoss: ProfitAndLossPercentage;
        profitAndLossString: SignAddedCommaFormattedString;
        stockName: string;
      },
      {
        rank: 5;
        profitAndLoss: ProfitAndLossPercentage;
        profitAndLossString: SignAddedCommaFormattedString;
        stockName: string;
      }
    ]
  | [];
type GetBarChartData = (data: RawData) => BarChartData;
type GetLabelsData = (data: RawData) => LabelsData;
type RankingProps<A extends Align> = {
  align: A;
  Chart: React.FC<BarChartProps>;
  data: RawData;
  children: ReturnType<React.FC<ProfitAndLossCardListProps>>;
  isShowToggleButton: boolean;
  toggle: () => void;
  currencyMode: CurrencyMode;
};

const ProfitAndLossCard: React.FC<ProfitAndLossCardProps> = ({
  stockCode,
  stockName,
  cashMargin,
  productType,
  profitAndLossAmount: profitAndLossAmount_,
  profitAndLossSign: profitAndLossSign_,
  profitAndLossRate: profitAndLossRate_,
  valuation: valuation_,
  currencyMode,
  foreign,
}) => {
  const isStockMargin = cashMargin === consts.CashMargin.信用;

  const unit = currencyMode === 'FOREIGN' && foreign ? foreign.currency : '円';
  const profitAndLossAmount =
    currencyMode === 'FOREIGN' && foreign
      ? foreign.profitAndLossAmount
      : profitAndLossAmount_;
  const profitAndLossSign =
    currencyMode === 'FOREIGN' && foreign
      ? foreign.profitAndLossSign
      : profitAndLossSign_;
  const profitAndLossRate =
    currencyMode === 'FOREIGN' && foreign
      ? foreign.profitAndLossRate
      : profitAndLossRate_;
  const valuation =
    currencyMode === 'FOREIGN' && foreign ? foreign.valuation : valuation_;
  const itemLink = `tradedetail/${stockCode}`;
  const bgColor = profitAndLossSign === 'plus' ? 'bg-red-400' : 'bg-sky-500';
  const detailClass = `p-2 ${
    isStockMargin ? 'mt-auto' : 'mt-2'
  } text-bluegray-50 rounded-7 ${bgColor}`;

  const dataFetchingState: DataFetchingState = getDataFetchingState(
    stockCode,
    productType === productTypes.投資信託
      ? AssetType.投資信託
      : AssetType.その他
  );
  const getDataFetchingTextStyle = getDataFetchingTextStyle_(dataFetchingState);

  const routing = useRouting();
  const goTradeDetail = (): void => {
    routing.pushRouting(
      { path: `/tradedetail/${stockCode}` },
      true,
      window.scrollY
    );
  };

  return (
    <>
      <li
        className="border-item flex flex-col p-0 bg-white border border-gray-300 rounded-7"
        style={{ width: '180px', minWidth: '180px' }}>
        <div className="flex flex-col flex-1 p-3">
          <div className="h-4 mb-1 flex items-center">
            <div
              className={`mr-3 text-xs text-bluegray-700 ${getDataFetchingTextStyle(
                ''
              )}`}>
              {stockCode}
            </div>
            <ProductTypesLabel
              productType={productType}
              cashMargin={cashMargin}
              className={'ml-auto'}
            />
          </div>
          <div
            className={`recommended-item-list-title ${getDataFetchingTextStyle(
              ''
            )}`}>
            {stockName}
          </div>
        </div>
        <div
          className="flex flex-col flex-1 p-3 border-t border-bluegray-200"
          style={{ minHeight: '155px' }}>
          {!isStockMargin ? (
            <>
              <div className="">
                <div
                  className="text-xs text-bluegray-500"
                  style={{ lineHeight: '14px' }}>
                  評価額
                </div>
                <div
                  className={`text-sm font-bold text-right text-bluegray-700 leading-4 ${getDataFetchingTextStyle(
                    ''
                  )}`}>
                  {valuation ?? '---'}
                  {unit}
                </div>
              </div>
            </>
          ) : (
            <></>
          )}
          <div className={`${detailClass} text-xs`}>
            <div>評価損益</div>
            <div className="text-sm font-bold text-right">
              {profitAndLossAmount}
              {unit}
            </div>
            <div className="mt-1">評価損益率</div>
            <div className="text-right">{profitAndLossRate}%</div>
          </div>
        </div>
        <div
          className="flex items-center justify-center border-t border-bluegrayslate-200"
          style={{ minHeight: '35px' }}>
          {dataFetchingState === 'Fetching' ||
          !isGoSymbolDetail(stockCode, productType) ? (
            <></>
          ) : (
            <div
              className="h-5 flex items-center text-xs text-link"
              onClick={(): void => goTradeDetail()}>
              取引詳細
            </div>
          )}
        </div>
      </li>
    </>
  );
};

const ProfitAndLossCardList: React.FC<ProfitAndLossCardListProps> = ({
  children,
}) => {
  return (
    <>
      <ul className="border-item-list border-item-horizontal-list asset-ranking-panel-list">
        {children}
      </ul>
    </>
  );
};

export const Chart: React.FC<BarChartProps> = ({ data, align }) => {
  const direction = align === 'left' ? 1 : align === 'right' ? -1 : 1;
  const barBestColor = align === ALIGN.INCREASE ? '#E77975' : '#62BBF3';
  const barColor = align === ALIGN.INCREASE ? '#F6CCCB' : '#C3E5FA';
  const options: GoogleChartOptions = {
    backgroundColor: 'transparent',
    hAxis: {
      direction,
      minValue: 0,
      ticks: [],
    },
    vAxis: {
      gridlines: {
        color: 'transparent',
      },
      direction: -1,
    },
    legend: {
      position: 'none',
    },
    chartArea: {
      left: 0,
      top: 0,
      height: '100%',
      width: '100%',
    },
  };

  const header = [
    'Rank',
    'ProfitAndLoss',
    { role: 'tooltip' },
    { role: 'style' },
  ];
  const dataWithTooltip = data.map(datum => {
    return datum.map((v, i, a) =>
      i === 2 ? `${getRank(a as BarChartRowData)}位: ${v}円` : v
    );
  });
  const styledData = dataWithTooltip.map((datum, i) => {
    return [...datum, i === 0 ? barBestColor : barColor];
  });

  if (data.length === 0) return null;
  return (
    <>
      <div className="asset-ranking-graph">
        <GoogleChart
          chartType="BarChart"
          options={options}
          data={[header, ...styledData]}
          height={'100%'}
          width={'100%'}
        />
      </div>
    </>
  );
};

const BarChartLabels: React.FC<BarChartLabelsProps> = ({ data }) => {
  if (data.length === 0) return null;
  return (
    <>
      <ul className="asset-ranking-label">
        {data.map((datum, i) => (
          <li className={`asset-ranking-label-item`} key={datum.rank}>
            {datum.rank}.
            <span
              className={`name ${i === 0 ? 'best' : ''} ${
                datum.stockName === '' ? 'nothing' : ''
              }`}>
              {datum.stockName === '' ? '―――' : datum.stockName}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
};

const NoRankingItem: React.FC<NoRankingItemProps> = ({ align }) => {
  const message = align === ALIGN.INCREASE ? '含み益のある' : '減った';
  return (
    <>
      <div
        className="w-full grid place-content-center text-sm text-black opacity-30"
        style={{ height: '200px' }}>
        {message}保有資産はありません。
      </div>
    </>
  );
};
const getBarChartData: GetBarChartData = data => {
  if (data.length === 0) return [];
  return data.map((datum, i) => {
    return [datum.rank, datum.profitAndLoss, datum.profitAndLossString];
  });
};
const getLabelsData: GetLabelsData = data => {
  if (data.length === 0) return [];
  return data.map(datum => {
    return { rank: datum.rank, stockName: datum.stockName };
  });
};
const Ranking: React.FC<RankingProps<Align> & {
  title: string;
}> = ({
  title,
  align,
  data,
  Chart,
  children,
  isShowToggleButton,
  ...currencyMode
}) => {
  const barChartData = getBarChartData(data);
  const barChartLabelsData = getLabelsData(data);
  if (data.length === 0) return <NoRankingItem align={align} />;
  return (
    <>
      <div className="flex">
        <SectionTitle>{title}</SectionTitle>
        <div className="flex flex-grow">
          <ToggleButton {...currencyMode} />
          <AboutExchangeRateModalButton isShowMessage className="ml-auto" />
        </div>
      </div>
      <div className="asset-ranking-block">
        <div className="asset-ranking-graph-block">
          {align === 'left' && <BarChartLabels data={barChartLabelsData} />}
          <Chart align={align} data={barChartData} />
          {align === 'right' && <BarChartLabels data={barChartLabelsData} />}
        </div>
        {children}
      </div>
    </>
  );
};
const IncreaseRanking: React.FC<RankingProps<
  typeof ALIGN.INCREASE
>> = props => {
  return <Ranking title="含み益TOP5" {...props} />;
};
const DecreaseRanking: React.FC<RankingProps<
  typeof ALIGN.DECREASE
>> = props => {
  return <Ranking title="含み損TOP5" {...props} />;
};

const SectionTitle: React.FC<TitleProps> = ({ children }) => {
  return (
    <>
      <div className="mb-2 text-lg font-bold">{children}</div>
    </>
  );
};

const Title: React.FC<TitleProps> = ({ children }) => {
  return (
    <>
      <div className="flex items-center mb-3 text-base font-bold text-bluegray-700">
        <img
          src="/static/images/report/icon-trophy.svg"
          alt=""
          className="mr-2"
        />
        {children}
      </div>
    </>
  );
};

const Note: React.FC<Record<string, unknown>> = () => (
  <div
    className="flex items-center text-xs text-bluegray-500"
    style={{ minHeight: '18px' }}>
    ※ランキングは保有銘柄のみ、評価損益額が高い銘柄・低い銘柄順にそれぞれ表示しております。
    <br />
    ※上位5銘柄以下は省略されております。
    <br />
    ※一部の投資信託は連携後、1〜2営業日ほどデータ反映が遅れる可能性があります。
  </div>
);

const Hint: React.FC<Record<string, unknown>> = () => (
  <div className="p-4 overflow-hidden">
    <div className="py-2 px-4 bg-bluegray-50 rounded-7">
      <div className="mb-1 text-xs text-teal-500 leading-6">活用のヒント</div>
      <div className="flex">
        <div className="h-12.5 w-12.5 min-w-12.5 mr-2 rounded-full">
          <img
            src="/static/images/advisor/ai_img_2.svg"
            className="rounded-full"
            alt=""
          />
        </div>
        <div className="text-sm text-bluegray-700">
          <div className="text-xs font-bold">商品ごとの共通点は？</div>
          <div className="mt-1 text-sm">
            国・業種など運用成績の良い銘柄・悪い銘柄の共通点はあるか着目してみましょう。
          </div>
        </div>
      </div>
    </div>
  </div>
);

const getRankingData: GetRankingData = data => {
  if (data.length === 0) return [];
  const trimmedData = (<T,>(length: number, data: T[], space: T): T[] => {
    if (data.length > length) return data.slice(0, length);
    if (data.length < length)
      return [
        ...data,
        ...Array.from({ length: length - data.length }).map(_ => space),
      ];
    return [...data];
  })(
    5,
    data.map(datum => ({
      profitAndLoss: datum.rawProfitAndLoss,
      profitAndLossString: datum.profitAndLossAmount,
      stockName: datum.stockName,
    })),
    {
      profitAndLoss: createProfitAndLossPercentage(0, 1),
      profitAndLossString: addSignCommaFormat(0),
      stockName: '',
    }
  );
  const ret: RawData = [
    {
      ...trimmedData[0],
      rank: 1,
    },
    {
      ...trimmedData[1],
      rank: 2,
    },
    {
      ...trimmedData[2],
      rank: 3,
    },
    {
      ...trimmedData[3],
      rank: 4,
    },
    {
      ...trimmedData[4],
      rank: 5,
    },
  ];
  return ret;
};
export const ProfitAndLossValuationRanking = ({
  PositionRanking,
}: {
  PositionRanking: api.ReportPositionRanking;
}) => {
  const positionToProfitAndLossCardProps = (
    position: api.PositionInfo,
    furthestRawProfitAndLossFrom0: number
  ): Omit<ProfitAndLossCardProps, 'currencyMode'> & {
    rawProfitAndLoss: ProfitAndLossPercentage;
  } => {
    const stockCode = position.Symbol;
    const stockName = position.SymbolName;
    const valuation =
      position.ValidationAmount === null
        ? null
        : commaFormat(position.ValidationAmount);
    const profitAndLossAmount = addSignCommaFormat(
      position.DiffValidationAmount
    );
    const profitAndLossSign = getSign(position.DiffValidationAmount ?? 0);
    const profitAndLossRate = addSignCommaFormat(position.DiffValidationRatio);
    const cashMargin = position.CashMargin;
    const rawProfitAndLoss = createProfitAndLossPercentage(
      position.DiffValidationAmount ?? 0,
      furthestRawProfitAndLossFrom0
    );
    const foreign: ProfitAndLossCardProps['foreign'] = isForeignStockTypes(
      position.ProductType
    )
      ? {
          currency: position.CurrencyUnit,
          valuation:
            position.ValidationAmountUSD === null
              ? null
              : commaFormat(position.ValidationAmountUSD),
          profitAndLossAmount: addSignCommaFormat(
            position.DiffValidationAmountUSD
          ),
          profitAndLossRate: addSignCommaFormat(position.DiffValidationRatio),
          profitAndLossSign: getSign(position.DiffValidationAmountUSD ?? 0),
        }
      : null;
    return {
      stockCode,
      stockName,
      valuation,
      profitAndLossAmount,
      profitAndLossSign,
      profitAndLossRate,
      cashMargin,
      rawProfitAndLoss,
      foreign,
      productType: position.ProductType,
    };
  };
  const increaseProfitAndLossData = sort(
    PositionRanking.IncreasedPositionList.map((datum, _, arr) => {
      const furthestRawProfitAndLossFrom0 = getFurthestFrom0(
        ...arr.map(v => v.DiffValidationAmount ?? 0)
      );
      return positionToProfitAndLossCardProps(
        datum,
        furthestRawProfitAndLossFrom0
      );
    }),
    (a, b) => -(a.rawProfitAndLoss - b.rawProfitAndLoss)
  );
  const decreaseProfitAndLossData = sort(
    PositionRanking.DecreasedPositionList.map((datum, _, arr) => {
      const furthestRawProfitAndLossFrom0 = getFurthestFrom0(
        ...arr.map(v => v.DiffValidationAmount ?? 0)
      );
      return positionToProfitAndLossCardProps(
        datum,
        furthestRawProfitAndLossFrom0
      );
    }),
    (a, b) => -(a.rawProfitAndLoss - b.rawProfitAndLoss)
  );

  const increaseRankingData = getRankingData(increaseProfitAndLossData);
  const decreaseRankingData = getRankingData(decreaseProfitAndLossData);
  const increaseCurrencyMode = useCurrencyMode();
  const decreaseCurrencyMode = useCurrencyMode();

  return (
    <>
      <div className="p-4 overflow-hidden">
        <Title>評価損益ランキング（保有資産別）</Title>
        <div className="section-block-divider" />
        <IncreaseRanking
          {...increaseCurrencyMode}
          Chart={Chart}
          align={ALIGN.INCREASE}
          isShowToggleButton={increaseProfitAndLossData.some(item =>
            isForeignStockTypes(item.productType)
          )}
          data={increaseRankingData}>
          <ProfitAndLossCardList>
            {increaseProfitAndLossData.map((p, i) => (
              <React.Fragment key={i}>
                <ProfitAndLossCard
                  {...p}
                  currencyMode={increaseCurrencyMode.currencyMode}
                />
              </React.Fragment>
            ))}
          </ProfitAndLossCardList>
        </IncreaseRanking>
        <div className="section-block-divider" />
        <DecreaseRanking
          {...decreaseCurrencyMode}
          Chart={Chart}
          align={ALIGN.DECREASE}
          isShowToggleButton={decreaseProfitAndLossData.some(item =>
            isForeignStockTypes(item.productType)
          )}
          data={decreaseRankingData}>
          <ProfitAndLossCardList>
            {decreaseProfitAndLossData.map((p, i) => (
              <ProfitAndLossCard
                key={i}
                {...p}
                currencyMode={decreaseCurrencyMode.currencyMode}
              />
            ))}
          </ProfitAndLossCardList>
        </DecreaseRanking>
        <Note />
      </div>
      <Hint />
      <div className="text-gray-400 text-sm px-4 pb-4">
        ※外国資産の前週評価額は前週末時点の参考レートを使用しています。
      </div>
    </>
  );
};

const getFurthestFrom0 = (...arr: number[]): number => {
  const minus = arr.filter(v => v < 0);
  const plus = arr.filter(v => v >= 0);
  const minusBiggest = minus.length === 0 ? 0 : Math.min(...minus);
  const plusBiggest = plus.length === 0 ? 0 : Math.max(...plus);
  return Math.abs(minusBiggest) > Math.abs(plusBiggest)
    ? minusBiggest
    : plusBiggest;
};
